/**
 * ErrorBoundary Component
 *
 * Catches errors in React component tree, especially:
 * - Lazy loading failures when offline
 * - Dynamic import errors
 * - Render errors
 *
 * Provides user-friendly error screen with retry option.
 */

import React, { Component, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { WifiOff, RefreshCw, AlertCircle } from "lucide-react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  isNetworkError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isNetworkError: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Check if it's a network/lazy loading error
    const isNetworkError =
      error.message?.includes("Failed to fetch") ||
      error.message?.includes("dynamically imported module") ||
      error.message?.includes("ERR_INTERNET_DISCONNECTED") ||
      error.message?.includes("network") ||
      error.message?.includes("NetworkError");

    return {
      hasError: true,
      error,
      isNetworkError,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    this.setState({
      errorInfo,
    });
  }

  handleRetry = () => {
    // Reset state and trigger re-render
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      isNetworkError: false,
    });

    // Force reload the page to re-fetch lazy loaded modules
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const isOnline = typeof navigator !== "undefined" ? navigator.onLine : true;

      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-secondary/20 to-accent/10 p-4">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                {this.state.isNetworkError ? (
                  <WifiOff className="h-5 w-5" />
                ) : (
                  <AlertCircle className="h-5 w-5" />
                )}
                {this.state.isNetworkError
                  ? "Erreur de connexion"
                  : "Une erreur est survenue"}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {this.state.isNetworkError ? (
                <>
                  <p className="text-muted-foreground">
                    Impossible de charger la page. Vérifiez votre connexion internet.
                  </p>
                  {!isOnline && (
                    <p className="text-sm text-orange-600">
                      Vous êtes actuellement hors ligne. Reconnectez-vous puis cliquez sur "Réessayer".
                    </p>
                  )}
                  {isOnline && (
                    <p className="text-sm text-blue-600">
                      Connexion rétablie. Cliquez sur "Réessayer" pour recharger la page.
                    </p>
                  )}
                </>
              ) : (
                <p className="text-muted-foreground">
                  Une erreur inattendue s'est produite. Veuillez réessayer.
                </p>
              )}

              <Button onClick={this.handleRetry} className="w-full">
                <RefreshCw className="h-4 w-4 mr-2" />
                Réessayer
              </Button>

              {process.env.NODE_ENV === "development" && this.state.error && (
                <details className="mt-4 text-xs">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                    Détails techniques (dev)
                  </summary>
                  <pre className="mt-2 p-2 bg-muted rounded text-wrap break-all">
                    {this.state.error.toString()}
                    {this.state.errorInfo?.componentStack}
                  </pre>
                </details>
              )}
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
